import { useState, useEffect } from 'react'
import { Row, Col, Tabs, Space, Divider } from 'antd'
import dayjs from 'dayjs'

import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _map from 'lodash/map'
import _size from 'lodash/size'
import _slice from 'lodash/slice'
import _some from 'lodash/some'
import _includes from 'lodash/includes'

import Title from 'components/Title'
import Image from 'components/Image'
import Link from 'components/Link'
import Paragraph from 'components/Paragraph'
import Icon from 'components/Icon'

import { getArticlesApi, getArticleTypesApi } from 'services/article'

import asyncComponent from 'utils/asyncComponent'

import Styled from './styled'

const AffixArticleCategories = asyncComponent(() =>
  import('components/Affix/AffixArticleCategories')
)
const Carousel = asyncComponent(() => import('components/Carousel'))
const CardArticleGrid = asyncComponent(() => import('components/Card/CardArticleGrid'))
const CardArticleList = asyncComponent(() => import('components/Card/CardArticleList'))

export default function Article() {
  const [articles, setArticles] = useState([])
  const [articleTypes, setArticleTypes] = useState([])

  const title = 'Our Community'
  const coverImage = 'https://d2jl6hozakvcbj.cloudfront.net/hero_aab339ff55.jpg'

  const getArticles = async () => {
    return await getArticlesApi()
      .then(res => res.data.data || [])
      .then(data => {
        setArticles(data)

        return data
      })
  }

  const getArticleTypes = async () => {
    return await getArticleTypesApi()
      .then(res => res.data.data || [])
      .then(data => {
        setArticleTypes(data)

        return data
      })
  }

  useEffect(() => {
    Promise.all([getArticles(), getArticleTypes()])
  }, [])

  const allCategories = _filter(articleTypes, c => !_includes(['review', 'popular'], c?.code))

  const mostPopularCategory = {
    ..._find(articleTypes, ['code', 'popular']),
    articles: _filter(articles, article => {
      return _some(article?.articleTypes, articleType => articleType?.code === 'popular')
    }),
  }

  const reviewCategory = {
    ..._find(articleTypes, ['code', 'review']),
    articles: _filter(articles, article => {
      return _some(article?.articleTypes, articleType => articleType?.code === 'review')
    }),
  }

  return (
    <Styled>
      <div className="blog-container">
        <div className="banner">
          <div className="shadow" />
          <Image src={coverImage} className="cover-image" />
          <Title level={1} className="title">
            {title}
          </Title>
        </div>

        <AffixArticleCategories />

        <div className="container">
          <div className="blog-content">
            <div className="most-popular">
              <div className="title">
                <Title level={2}>{mostPopularCategory?.name}</Title>
              </div>

              <div className="card-content">
                <Row
                  gutter={[
                    { xs: 10, sm: 10, md: 16, lg: 45 },
                    { xs: 16, sm: 16, md: 16, lg: 16 },
                  ]}
                >
                  {_map(_slice(mostPopularCategory?.articles, 0, 2), (item, index) => {
                    const slug = item?.code
                    const link = `/blog/${mostPopularCategory?.code}/${slug}`

                    return (
                      <Col span={24} sm={12} md={12} lg={8} key={index}>
                        <Link href={link}>
                          <CardArticleGrid
                            categories={item?.categories}
                            title={item?.title}
                            detail={item?.shortDescription}
                            image={item?.thumbnailImage?.[0]?.url}
                          />
                        </Link>
                      </Col>
                    )
                  })}

                  <Col span={24} lg={8}>
                    <Row gutter={[{ xs: 0, sm: 10, md: 16, lg: 16 }, 0]}>
                      {_map(_slice(mostPopularCategory?.articles, 2, 5), (item, index) => {
                        const slug = item?.code
                        const link = `/blog/${mostPopularCategory?.code}/${slug}`
                        const isLast =
                          _size(_slice(mostPopularCategory?.articles, 2, 5)) === index + 1

                        return (
                          <Col span={24} sm={12} lg={24} key={index}>
                            <Link href={link}>
                              <CardArticleList
                                title={item?.title}
                                image={item?.thumbnailImage?.[0]?.url}
                                date={
                                  item?.updatedAt
                                    ? dayjs(item?.updatedAt).format('DD MMMM YYYY')
                                    : null
                                }
                              />
                            </Link>
                            {!isLast && <Divider />}
                          </Col>
                        )
                      })}
                    </Row>
                  </Col>
                </Row>
              </div>
            </div>

            <div className="review">
              <div className="title">
                <Title level={2}>{reviewCategory?.name}</Title>
              </div>

              <div className="card-content">
                <Carousel
                  options={{
                    slidesPerView: 1,
                    pagination: false,
                    spaceBetween: 0,
                    navigation: {
                      nextEl: '.carousel-button-next',
                      prevEl: '.carousel-button-prev',
                    },
                    breakpoints: {
                      768: {
                        slidesPerView: 2,
                        spaceBetween: 16,
                      },
                      992: {
                        slidesPerView: 3,
                        spaceBetween: 45,
                      },
                    },
                  }}
                  items={reviewCategory?.articles}
                  uiRenderer={item => {
                    const slug = item?.code
                    const link = `/blog/${reviewCategory?.code}/${slug}`

                    return (
                      <Link href={link}>
                        <CardArticleGrid
                          categories={item?.categories}
                          title={item?.title}
                          detail={item?.shortDescription}
                          image={item?.thumbnailImage?.[0]?.url}
                          date={
                            item?.updatedAt ? dayjs(item?.updatedAt).format('DD MMMM YYYY') : null
                          }
                        />
                      </Link>
                    )
                  }}
                />

                {_size(reviewCategory?.articles) > 3 && (
                  <div className="carousel-button-block">
                    <div className="carousel-button carousel-button-prev">
                      <Icon.ArrowRightShort />
                    </div>
                    <div className="carousel-button carousel-button-next">
                      <Icon.ArrowRightShort />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="article-content">
              <div className="title">
                <Title level={2}>บทความ</Title>
              </div>

              <Tabs>
                {_map(allCategories, category => {
                  const title = category?.name
                  const categorySlug = category?.code

                  const categoryArticles = _filter(articles, article => {
                    return _some(
                      article?.articleTypes,
                      articleType => articleType?.code === categorySlug
                    )
                  })
                  const categoryLink = `/blog/${categorySlug}`

                  return (
                    <Tabs.TabPane tab={title} key={categorySlug}>
                      <div className="grid-container">
                        <div className="button-block">
                          <Link href={categoryLink}>
                            <Space size={4} align="center">
                              <Paragraph medium className="text-button">
                                ดูบทความทั้งหมด
                              </Paragraph>
                              <Icon.ChevronRight className="icon-button" />
                            </Space>
                          </Link>
                        </div>

                        <Row
                          gutter={[
                            { xs: 10, sm: 10, md: 16, lg: 45 },
                            { xs: 16, sm: 16, md: 16, lg: 16 },
                          ]}
                        >
                          {_map(_slice(categoryArticles, 0, 3), (item, index) => {
                            const slug = item?.code
                            const link = `/blog/${categorySlug}/${slug}`

                            return (
                              <Col span={24} sm={12} md={12} lg={8} key={index}>
                                <Link href={link}>
                                  <CardArticleGrid
                                    categories={item?.categories}
                                    title={item?.title}
                                    detail={item?.shortDescription}
                                    image={item?.thumbnailImage?.[0]?.url}
                                    date={
                                      item?.updatedAt
                                        ? dayjs(item?.updatedAt).format('DD MMMM YYYY')
                                        : null
                                    }
                                  />
                                </Link>
                              </Col>
                            )
                          })}
                        </Row>
                      </div>
                    </Tabs.TabPane>
                  )
                })}
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </Styled>
  )
}
